import { action, computed, observable, set } from "mobx";
import moment from "moment";
import Model from "~/models/Model";
import InvoiceRow from "~/models/Invoice/InvoiceRow";
import InvoiceTax from "~/models/Invoice/InvoiceTax";
import InvoicePayment from "~/models/Invoice/InvoicePayment";
import InvoicePaymentLog from "~/models/Invoice/InvoicePaymentLog";
import InvoicePaymentStatus from "~/constants/Invoice/InvoicePaymentStatus";

class Invoice extends Model {
  /**
   * @property {number|null}
   */
  @observable invoiceId = null;

  /**
   * @property {Array}
   */
  @observable invoiceTaxs = [];

  /**
   * @property {Array}
   */
  @observable invoiceRows = [];

  /**
   * @property {Array}
   */
  @observable invoicePayments = [];

  /**
   * @property {Array}
   */
  @observable invoicePaymentLogs = [];

  /**
   * @property {string|null}
   */
  @observable creationDts = null;

  /**
   * @property {string|null}
   */
  @observable modificationDts = null;

  /**
   * Set new data to our model
   * @override
   * @param {object} data
   */
  @action
  setData(data) {
    const {
      invoiceTaxs,
      invoiceRows,
      invoicePayments,
      invoicePaymentLogs,
      creationDts,
      modificationDts,
      ...rest
    } = data;
    set(this, rest);

    this.invoiceTaxs = [];

    if (invoiceTaxs) {
      invoiceTaxs.forEach(invoiceTaxArray => {
        const invoiceTax = new InvoiceTax(invoiceTaxArray);

        this.invoiceTaxs.push(invoiceTax);
      });
    }

    this.invoiceRows = [];

    if (invoiceRows) {
      invoiceRows.forEach(invoiceRowArray => {
        const invoiceRow = new InvoiceRow(invoiceRowArray);

        this.invoiceRows.push(invoiceRow);
      });
    }

    this.invoicePayments = [];

    if (invoicePayments) {
      invoicePayments.forEach(invoicePaymentArray => {
        const invoicePayment = new InvoicePayment(invoicePaymentArray);

        this.invoicePayments.push(invoicePayment);
      });
    }

    this.invoicePaymentLogs = [];

    if (invoicePaymentLogs) {
      invoicePaymentLogs.forEach(invoicePaymentLogArray => {
        const invoicePaymentLog = new InvoicePaymentLog(invoicePaymentLogArray);

        this.invoicePaymentLogs.push(invoicePaymentLog);
      });
    }

    this.creationDts = creationDts ? moment.unix(creationDts) : null;
    this.modificationDts = modificationDts ? moment.unix(modificationDts) : null;
  }

  @computed
  get subtotal() {
    let subtotal = 0;

    this.invoiceRows.forEach(invoiceRow => {
      subtotal = Math.round(subtotal * 100 + parseFloat(invoiceRow.amount) * 100) / 100;
    });

    return subtotal;
  }

  @computed
  get totalAmountWithTaxes() {
    let totalAmountWithTaxes = this.subtotal;

    this.invoiceTaxs.forEach(invoiceTax => {
      totalAmountWithTaxes = Math.round(totalAmountWithTaxes * 100 + parseFloat(invoiceTax.amount) * 100) / 100;
    });

    return totalAmountWithTaxes;
  }

  @computed
  get amountPaid() {
    let amountPaid = 0;

    this.invoicePayments.forEach(invoicePayment => {
      amountPaid = Math.round(amountPaid * 100 + parseFloat(invoicePayment.amount) * 100) / 100;
    });

    return amountPaid;
  }

  @computed
  get paymentStatus() {
    if (this.amountPaid === 0) {
      return InvoicePaymentStatus.UNPAID;
    }

    return this.totalAmountWithTaxes === this.amountPaid
      ? InvoicePaymentStatus.PAID
      : InvoicePaymentStatus.PARTIALLY_PAID;
  }

  @computed
  get paymentStatusTranslationKey() {
    if (this.paymentStatus === InvoicePaymentStatus.PAID) {
      return "paid";
    }

    if (this.paymentStatus === InvoicePaymentStatus.PARTIALLY_PAID) {
      return "partiallyPaid";
    }

    return "unpaid";
  }
}

export default Invoice;
